import type {
    ContactVersion,
    LawFirmContactDetail,
    LawFirmContactListParams,
    LawFirmContactListResponse,
} from '@common/types/contacts';
import { useQuery } from '@tanstack/react-query';
import {
    fetchContactById,
    fetchContactsByLawFirmId,
    fetchContactsByProspectVersionId,
} from '@/services/ContactService';

interface ListParams extends LawFirmContactListParams {
    lawFirmId?: number;
    enabled?: boolean;
}

export function useContactsByLawFirmId({
    lawFirmId,
    page = 0,
    pageSize = 10,
    search,
    enabled = true,
}: ListParams) {
    return useQuery<LawFirmContactListResponse>({
        queryKey: ['contacts', 'law-firm', lawFirmId, { page, pageSize, search }],
        queryFn: async () => {
            if (!lawFirmId) {
                return { items: [], total: 0 };
            }
            return fetchContactsByLawFirmId(lawFirmId, { page, pageSize, search });
        },
        enabled: enabled && !!lawFirmId,
        staleTime: 5 * 60_000,
        placeholderData: (previousData) => previousData,
    });
}

interface DetailParams {
    contactId?: string;
    enabled?: boolean;
}

export function useContactById({ contactId, enabled = true }: DetailParams) {
    return useQuery<LawFirmContactDetail>({
        queryKey: ['contacts', contactId],
        queryFn: async () => {
            if (!contactId) {
                throw new Error('Contact ID is required');
            }
            return fetchContactById(contactId);
        },
        enabled: enabled && !!contactId,
        staleTime: 5 * 60_000,
    });
}

export function useContactsByProspectVersionId(prospectVersionId?: number) {
    return useQuery<ContactVersion[]>({
        queryKey: ['contacts', 'prospect-version', prospectVersionId],
        queryFn: async () => {
            if (!prospectVersionId) {
                throw new Error('prospectVersionId is required');
            }
            return fetchContactsByProspectVersionId(prospectVersionId);
        },
        enabled: !!prospectVersionId,
        staleTime: 5 * 60_000,
    });
}
